<script setup>
import { computed } from 'vue'

const props = defineProps({
  title: {
    type: String,
    default: 'Warum unsere Fotoboxen perfekt für Ihre Veranstaltung sind'
  },
  subtitle: {
    type: String,
    default: 'Unsere speziell konzipierten Fotoboxen bieten höchste Qualität und sorgen für unvergessliche Momente auf Ihrer Feier.'
  },
  features: {
    type: Array,
    default: () => [
      {
        title: 'Hochwertiger Drucker',
        description: 'Sofortige Ausdrucke in bester Qualität für Ihre Gäste',
        icon: 'printer'
      },
      {
        title: 'Individuelle Layouts',
        description: 'Designs passend zu Ihrem Veranstaltungsthema und -farben',
        icon: 'template'
      },
      {
        title: 'Unbegrenzte Fotos',
        description: 'Keine Begrenzung der Anzahl an digitalen Fotos',
        icon: 'infinity'
      },
      {
        title: 'Digitale Galerie',
        description: 'Alle Fotos in einer Online-Galerie zum Teilen und Herunterladen',
        icon: 'cloud'
      },
      {
        title: 'Requisitenpaket',
        description: 'Große Auswahl an hochwertigen Requisiten',
        icon: 'hat'
      },
      {
        title: 'Professioneller Support',
        description: 'Lieferung, Aufbau und technische Betreuung inklusive',
        icon: 'support'
      }
    ]
  },
  locationName: {
    type: String,
    default: ''
  },
  eventType: {
    type: String,
    default: 'Veranstaltung'
  }
})

// Wenn ein Standortname übergeben wurde, füge ihn zum Titel hinzu
const displayTitle = computed(() => {
  if (props.locationName) {
    return `Warum unsere Fotoboxen perfekt für Ihre ${props.eventType || 'Veranstaltung'} in ${props.locationName} sind`
  }
  return props.title
})

// Funktion zum Rendern des passenden Icons basierend auf dem Icon-Namen
const getIconSvg = (iconName) => {
  const icons = {
    printer: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6">
      <polyline points="6 9 6 2 18 2 18 9"></polyline>
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
      <rect x="6" y="14" width="12" height="8"></rect>
    </svg>`,
    
    template: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
      <line x1="3" y1="9" x2="21" y2="9"></line>
      <line x1="9" y1="21" x2="9" y2="9"></line>
    </svg>`,
    
    infinity: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6">
      <path d="M18.178 8c5.096 0 5.096 8 0 8-5.095 0-7.133-8-12.739-8-4.585 0-4.585 8 0 8 5.606 0 7.644-8 12.74-8z"></path>
    </svg>`,
    
    cloud: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6">
      <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"></path>
    </svg>`,
    
    hat: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6">
      <path d="M2 8l10-5l10 5l-10 5z"></path>
      <path d="M12 16v7"></path>
      <path d="M5 11.5v5.5c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2v-5.5"></path>
    </svg>`,
    
    support: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6">
      <path d="M21 6h-8a2 2 0 0 1-2-2V3a2 2 0 0 0-2-2H3a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2z"></path>
      <line x1="10" y1="4" x2="10" y2="16"></line>
    </svg>`,
    
    camera: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
      <circle cx="12" cy="13" r="4"></circle>
    </svg>`,
    
    party: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6">
      <path d="M5.8 11.3L2 22l10.7-3.79"></path>
      <path d="M4 3h.01"></path>
      <path d="M22 8h.01"></path>
      <path d="M15 2h.01"></path>
      <path d="M22 20h.01"></path>
      <path d="M22 2l-2.24 3.37a2 2 0 0 0 2.24 2.93L22 2z"></path>
      <path d="M17 8l-4.74 7.11a2 2 0 0 0 3.19 2.37L17 8z"></path>
      <path d="M11 15l-7 5"></path>
    </svg>`,
    
    download: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
      <polyline points="7 10 12 15 17 10"></polyline>
      <line x1="12" y1="15" x2="12" y2="3"></line>
    </svg>`
  }
  
  // Standardicon falls das angeforderte nicht gefunden wird
  return icons[iconName] || icons['party']
}
</script>

<template>
  <section class="py-16 bg-white">
    <div class="container mx-auto px-4">
      <div class="text-center mb-12">
        <h2 class="heading-2 mb-4">{{ displayTitle }}</h2>
        <p class="text-lg text-gray-600 max-w-3xl mx-auto">{{ subtitle }}</p>
      </div>
      
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <div v-for="(feature, index) in features" :key="index" class="p-6 border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
          <div class="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <div class="text-primary" v-html="getIconSvg(feature.icon)"></div>
          </div>
          <h3 class="text-xl font-semibold mb-2">{{ feature.title }}</h3>
          <p class="text-gray-600">{{ feature.description }}</p>
        </div>
      </div>
    </div>
  </section>
</template>